import React, {useEffect, useReducer} from 'react'
import styled from 'styled-components'
import {
  StyledWorkspaceActionButton as ActionButtonContainer,
  StyledWorkspaceContainer as Container,
  StyledWorkspaceFilter as Filter,
  StyledWorkspaceFloorList as FloorListContainer,
  StyledWorkspaceFloorPlan as FloorPlanContainer,
  StyledWorkspaceHeader as WorkspaceHeader
} from '../Workspace/StyledWorkspace'

import MeetingRoomMiniFilter from './Filter/MeetingRoomMiniFilter'
import FloorList, {DIRECTION} from '../../components/FloorList/FloorList'
import FloorPlan from '../FloorPlan/FloorPlan'
import ActionButton from '../../components/ActionButton/ActionButton'
import Header from '../../components/Header/Header'
import MediaQuery from 'react-responsive'
import {Layer} from 'grommet/components/Layer'
import MeetingRoomFilter from './Filter/MeetingRoomFilter'
import {generateRecurrenceXml} from '../../utils/recurrence'
import {formatDate} from '../../utils/time'
import {
  DASHBOARD,
  KIOSK_CONTAINER,
  MEETING_SPACES_SEQUENCE
} from '../../constants/const'
import MeetingSpacesList from './MeetingSpacesList'
import BuildingList from '../BuildingList/BuildingList'
import HeaderButton from '../../components/Header/HeaderButton'
import ActionTitle from '../../components/Header/ActionTitle'
import {useRecurrenceState} from '../../context/RecurrenceContext'
import {useUIDispatch, useUIState} from '../../context/UIContext'
import {fetchBuilding, fetchBuildingFloors} from '../../utils/buildings'
import {fetchAvailableRoomReservations} from '../../api/reservations'
import {getBookingTimes, useConfigState} from '../../context/ConfigContext'
import {_L, useLocaleState} from '../../context/LocaleContext'
import {useMeetingDispatch, useMeetingState} from '../../context/MeetingContext'
import {fetchRoom} from '../../api/room'
import {isEmpty} from '../../utils/helpers'
import {useDrawingDispatch, useDrawingState} from '../../context/DrawingContext'
import {useKioskTimer} from '../../hooks/kiosk-timer'
import {highlightAvailableRooms, resetHighlights} from '../../utils/highlight'
import {useDebounce} from 'use-debounce'

const FilterContainer = styled.div`
  width: 90vw;

  @media (max-width: 420px) {
    height: 100%;
    width: 100%;
  }
`

const ModalFilter = ({
  margin,
  bookingTimes,
  onCloseFilter,
  onSearch,
  responsive,
  bookingRangeByFloor,
  isLoading,
  capacityByFloor,
  ...rest
}) => (
  <Layer responsive={responsive} {...rest}>
    <FilterContainer>
      <MeetingRoomFilter
        showClose
        onCloseFilter={onCloseFilter}
        onSearch={onSearch}
        bookingRangeByFloor={bookingRangeByFloor}
        isLoading={isLoading}
        capacityByFloor={capacityByFloor}
      />
    </FilterContainer>
  </Layer>
)

const initialState = {
  floors: [],
  building: {blId: '', name: ''},
  availableRooms: {},
  showListView: false,
  showFilter: false,
  showActionButton: true,
  showBuildingList: false,
  showNotification: false,
  selectedRoom: {},
  capacityByFloor: {},
  bookingRangeByFloor: {},
  isLoading: false
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'setData':
      return {
        ...state,
        ...action.payload
      }

    case 'setAvailableRooms':
      return {
        ...state,
        availableRooms: action.payload
      }

    case 'setLoading':
      return {
        ...state,
        isLoading: action.payload
      }
    default:
      throw new Error('Unexpected action')
  }
}

const getAvailableReservationParameters = ({
  meetingRecurrence,
  search,
  building
}) => {
  const {
    date_start,
    date_end,
    recurrenceType,
    frequency,
    frequencyDay,
    weekDays
  } = meetingRecurrence

  const {bl_id, fl_id, time_start, time_end, capacity, resources} = search

  const recurrence = generateRecurrenceXml({
    recurrenceType,
    weekDays,
    frequencyDay,
    frequency
  })

  let buildingCode = bl_id || building.blId
  return {
    date_start: formatDate(date_start, 'YYYY-MM-DD'),
    date_end: formatDate(date_end, 'YYYY-MM-DD'),
    recurrence,
    bl_id: buildingCode,
    fl_id,
    time_start,
    time_end,
    capacity: capacity === 1 ? 0 : capacity,
    resource_std: resources.map(resource_std => ({
      resource_std: resource_std.value
    }))
  }
}

const MeetingRoom = ({parentPage}) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const localeState = useLocaleState()
  const meetingRecurrence = useRecurrenceState()
  const {search} = useMeetingState()

  const {
    building,
    floors,
    availableRooms,
    showListView,
    showFilter,
    showActionButton,
    showBuildingList,
    showNotification,
    selectedRoom,
    capacityByFloor,
    bookingRangeByFloor,
    isLoading
  } = state

  const uiDispatch = useUIDispatch()
  const uiState = useUIState()
  const configState = useConfigState()
  const bookingTimes = getBookingTimes(configState)
  const drawingDispatch = useDrawingDispatch()
  const {status} = useDrawingState()

  const [debouncedSearch] = useDebounce(search, 500)

  const {activeFloor, kioskId, homeFloor, isKiosk} = uiState


  const {blId, flId} = activeFloor
  const {hasSvgData, blId: statusBlId, flId: statusFlId} = status

  const BookingPrompt = ({rmId, category, localeState, onBook}) => (
    <>
      <StyledTitle>
        {rmId} &middot; {category}
      </StyledTitle>

      <StyledButton onClick={onBook} data-testid="meeting-book-button">
        {_L({key: 'Book', localeState})}
      </StyledButton>
    </>
  )

  useEffect(
    () => {
      let isMounted = true
      async function fetchMeetingData() {
        if (blId) {
          resetHighlights({drawingDispatch})
          const [building, floors] = await Promise.all(
            [fetchBuilding, fetchBuildingFloors].map(fn => fn({blId}))
          )

          const params = getAvailableReservationParameters({
            meetingRecurrence,
            search: debouncedSearch,
            building
          })
          params.bl_id = blId

          const {
            availableRooms,
            capacityByFloor,
            bookingRangeByFloor
          } = await fetchAvailableRoomReservations({params})

          if (isMounted) {
            dispatch({
              type: 'setData',
              payload: {
                building,
                floors,
                capacityByFloor,
                availableRooms,
                bookingRangeByFloor
              }
            })
          }
        }
      }

      fetchMeetingData()

      return () => {
        isMounted = false
      }
    },
    [blId, drawingDispatch, meetingRecurrence, debouncedSearch]
  )

  useEffect(
    () => {
      let availableRoomCodes = availableRooms[flId]
        ? availableRooms[flId].roomCodes
        : []
      highlightAvailableRooms({
        drawingDispatch,
        availableRooms: availableRoomCodes
      })
    },
    [flId, availableRooms, drawingDispatch]
  )

  useEffect(
    () => {
      if (
        statusBlId === blId &&
        statusFlId === flId &&
        availableRooms &&
        availableRooms[statusFlId] &&
        !hasSvgData
      ) {
        dispatch({type: 'setData', payload: {showListView: true}})
      } else {
        dispatch({type: 'setData', payload: {showListView: false}})
      }
    },
    [hasSvgData, statusBlId, statusFlId, blId, flId, availableRooms]
  )

  // Kiosk Timer
  const timeOutExpired = () => {
    const isConferenceKiosk = kioskId.eq_std === 'KIOSK-CONFERENCE'
    uiDispatch({
      type: 'setContent',
      payload: {
        content: isConferenceKiosk ? 'CONFERENCE_ROOM' : KIOSK_CONTAINER
      }
    })
    uiDispatch({type: 'setActiveFloor', payload: homeFloor})
  }

  const {message, onClickHandler} = useKioskTimer({
    enable: isKiosk,
    inUseDelay: 60000,
    timeoutDelay: 10000,
    timeoutExpirationHandler: timeOutExpired
  })

  const onFloorChange = ({bl_id, fl_id}) => {
    uiDispatch({
      type: 'setActiveFloor',
      payload: {
        blId: bl_id,
        flId: fl_id
      }
    })
  }

  const onBook = () =>
    uiDispatch({
      type: 'setContent',
      payload: {
        content: MEETING_SPACES_SEQUENCE,
        props: {room: selectedRoom},
        parentPage: parentPage || 'MEETING_SPACE'
      }
    })

  const onRoomClick = async ({room}) => {
    const {blId, flId, rmId, category} = await fetchRoom(room)
    dispatch({
      type: 'setData',
      payload: {selectedRoom: {blId, flId, rmId, category}}
    })
  }

  const onShowList = () =>
    dispatch({type: 'setData', payload: {showListView: !showListView}})

  const onShowFilter = () =>
    dispatch({type: 'setData', payload: {showFilter: true}})

  const onCloseFilter = () =>
    dispatch({type: 'setData', payload: {showFilter: false}})

  const onCancelMeetingBooking = () => {
    uiDispatch({
      type: 'showSearch',
      payload: {show: false, type: null, options: {}}
    })
    uiDispatch({
      type: 'setContent',
      payload: {content: isKiosk ? KIOSK_CONTAINER : parentPage || DASHBOARD}
    })
  }

  const onBuildingClick = () => {
    dispatch({
      type: 'setData',
      payload: {
        showListView: false,
        showFilter: false,
        showActionButton: false,
        showBuildingList: true
      }
    })
  }

  const onBuildingSelect = async blId => {
    uiDispatch({
      type: 'setActiveFloor',
      payload: {
        blId,
        flId: ''
      }
    })

    const {
      availableRooms,
      capacityByFloor
    } = await fetchAvailableRoomReservations({blId})

    dispatch({
      type: 'setData',
      payload: {availableRooms, capacityByFloor}
    })

    let [firstFloor] = Object.keys(availableRooms)

    if (firstFloor) {
      uiDispatch({
        type: 'setActiveFloor',
        payload: {
          blId,
          flId: firstFloor
        }
      })

      dispatch({
        type: 'setData',
        payload: {
          showListView: false,
          showFilter: false,
          showActionButton: false,
          showBuildingList: false
        }
      })
    } else {
      dispatch({type: 'setData', payload: {showNotification: true}})
    }
  }

  const onMeetingSelect = ({
    bl_id,
    fl_id,
    rm_id,
    config_id,
    rm_arrange_type_id
  }) => {
    const room = {
      blId: bl_id,
      flId: fl_id,
      rmId: rm_id,
      configId: config_id,
      rmArrangeTypeId: rm_arrange_type_id
    }

    uiDispatch({
      type: 'setContent',
      payload: {content: 'MEETING_SPACES_SEQUENCE', props: {room}, parentPage}
    })
  }

  const onSearch = async () => {
    // Convert params to search object
    dispatch({type: 'setLoading', payload: true})
    const params = getAvailableReservationParameters({
      meetingRecurrence,
      search,
      building
    })

    const {
      availableRooms,
      capacityByFloor,
      bookingRangeByFloor
    } = await fetchAvailableRoomReservations({params})

    dispatch({
      type: 'setData',
      payload: {
        availableRooms,
        showFilter: false,
        capacityByFloor,
        bookingRangeByFloor,
        isLoading: false
      }
    })

    if (isEmpty(availableRooms)) {
      uiDispatch({type: 'setActiveFloor', payload: {blId: '', flId: ''}})
    } else {
      if (activeFloor.blId === '' && activeFloor.flId === '') {
        let [firstFloor] = Object.keys(availableRooms)
        const {bl_id} = search
        let buildingCode = bl_id || building.blId
        uiDispatch({
          type: 'setActiveFloor',
          payload: {blId: buildingCode, flId: firstFloor}
        })
      }
    }
  }

  const onCloseNotification = () =>
    dispatch({type: 'setData', payload: {showNotification: false}})

  const headerText = _L({key: 'Meeting Space Booking', localeState})
  return (
    <Container onClick={onClickHandler}>
      <WorkspaceHeader>
        <Header
          leftContent={
            <ActionTitle title={headerText} buildingName={building.name} />
          }
          rightContent={
            <HeaderButton
              onClick={onCancelMeetingBooking}
              label={_L({key: 'Cancel', localeState})}
            />
          }
        />
      </WorkspaceHeader>

      {!showBuildingList && (
        <FloorListContainer>
          <MediaQuery query="only screen and (max-device-width: 1023px) and (orientation: portrait)">
            {matches => (
              <FloorList
                buildingCode={building.blId}
                floorList={floors}
                itemsPerFloor={availableRooms}
                onSelected={onFloorChange}
                selectedFloor={activeFloor}
                direction={matches ? DIRECTION.HORIZONTAL : DIRECTION.VERTICAL}
                availableOnly
                onBuildingClick={onBuildingClick}
                showBuilding
              />
            )}
          </MediaQuery>
        </FloorListContainer>
      )}

      {showBuildingList ? (
        <BuildingList
          onBuildingSelect={onBuildingSelect}
          showNotification={showNotification}
          onCloseNotification={onCloseNotification}
        />
      ) : (
        <Filter>
          <MediaQuery query="only screen and (max-device-width: 1023px) and (orientation: portrait)">
            {matches =>
              matches ? (
                <MeetingRoomMiniFilter onShowFilter={onShowFilter} />
              ) : (
                <MeetingRoomFilter
                  onSearch={onSearch}
                  capacityByFloor={capacityByFloor}
                  bookingRangeByFloor={bookingRangeByFloor}
                  isLoading={isLoading}
                />
              )
            }
          </MediaQuery>
        </Filter>
      )}

      {showListView ? (
        <MeetingSpacesList
          activeFloor={activeFloor}
          recurrence={meetingRecurrence}
          onMeetingSelect={onMeetingSelect}
        />
      ) : (
        <FloorPlanContainer showBuildingList={showBuildingList}>
          <FloorPlan
            promptContents={BookingPrompt({
              rmId: selectedRoom.rmId,
              category: selectedRoom.category,
              localeState,
              onBook
            })}
            onRoomClick={onRoomClick}
          />
        </FloorPlanContainer>
      )}

      {showActionButton && (
        <ActionButtonContainer>
          <ActionButton
            icon={showListView ? 'FLOOR_PLAN' : 'LIST'}
            backgroundColor="white"
            onClick={onShowList}
          />
        </ActionButtonContainer>
      )}

      {showFilter && (
        <MediaQuery maxDeviceWidth={420}>
          {matches => (
            <ModalFilter
              bookingTimes={bookingTimes}
              onCloseFilter={onCloseFilter}
              onSearch={onSearch}
              responsive={matches}
              bookingRangeByFloor={bookingRangeByFloor}
              isLoading={isLoading}
              capacityByFloor={capacityByFloor}
            />
          )}
        </MediaQuery>
      )}
      {message}
    </Container>
  )
}

export default MeetingRoom

const StyledText = styled.div`
  font-size: 16px;
  font-weight: 600;
`
const StyledTitle = styled(StyledText)`
  text-align: center;
  margin-bottom: 18px;
`

const StyledButton = styled.button`
  font-size: 16px;
  font-weight: 600;
  box-sizing: border-box;
  background-color: #3395fe;
  border: 2px solid #3395fe;
  color: #ffffff;
  height: 46px;
  border-radius: 6px;
  outline: none;
  width: 90%;
`
